import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from '../../auth/auth.service';
import { ToastService } from '../../toast/toast.service';
import { ThemeService } from '../../theme/theme.service';

export type ShellNavItem = 'dashboard' | 'clients' | 'account';

export interface ShellNavLink {
  id: string;
  label: string;
  icon: string;
  route: string;
}

const SUPERADMIN_NAV_LINKS: ShellNavLink[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'icon-layout-dashboard', route: '/dashboard' },
  { id: 'clients', label: 'Organisations', icon: 'icon-building', route: '/org-list' },
];

/**
 * App chrome shared by every authenticated screen: collapsible sidebar nav + topbar.
 * Page content is projected into `.aa-content`. Lives in SharedModule (not CoreModule)
 * because it's instantiated once per routed page, not an app-root singleton.
 *
 * `navItems` defaults to the Superadmin sidebar so every existing superadmin page keeps
 * working unchanged; the admin shell overrides it with its own link set instead of
 * duplicating this whole component just to show a different sidebar.
 */
@Component({
    selector: 'aa-shell',
    templateUrl: './shell.component.html',
    styleUrls: ['./shell.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ShellComponent {
  @Input() activeNavItem = 'clients';
  @Input() navItems: ShellNavLink[] = SUPERADMIN_NAV_LINKS;

  navOpen = false;
  logoFailed = false;
  userMenuOpen = false;

  constructor(
    private readonly auth: AuthService,
    private readonly router: Router,
    private readonly toast: ToastService,
    private readonly theme: ThemeService
  ) {}

  /** Shows what a click switches to: a moon in light mode, a sun in dark mode */
  get themeIcon(): string {
    return this.theme.mode === 'dark' ? '#icon-sun' : '#icon-moon';
  }

  get themeLabel(): string {
    return this.theme.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
  }

  toggleTheme(): void {
    this.theme.toggle();
  }

  /** Real name once set via My Details; otherwise derived from the logged-in email. */
  get userName(): string {
    return this.auth.getName();
  }

  get userInitial(): string {
    return this.userName.charAt(0).toUpperCase() || 'T';
  }

   /** Superadmin and user each have their own My Details page; the admin role doesn't have
   *  one in the mockup, so the dropdown item is hidden rather than pointing somewhere wrong. */
  get myDetailsRoute(): string | null {
    switch (this.auth.getUserType()) {
      case 'superadmin':
        return '/my-details';
      case 'user':
        return '/user/my-details';
      default:
        return null;
    }
  }

  toggleNav(): void {
    this.navOpen = !this.navOpen;
  }

  closeNav(): void {
    this.navOpen = false;
  }

  toggleUserMenu(): void {
    this.userMenuOpen = !this.userMenuOpen;
  }

  logOut(): void {
    this.userMenuOpen = false;
    this.auth.logout();
    this.router.navigateByUrl('/login');
    this.toast.show('Successful Logout', 'success');
  }
}
